import React, { useState } from 'react';
import { createKakaoMarker } from './pin';
import { useAuth, ROLES } from '../contexts/AuthContext';
import AddCard from './add_card';
import { formatFloorForDisplay } from '../utils/formatFloor';

export function getRoadviewPageUrl(lat, lng) {
  return `https://map.kakao.com/link/roadview/${lat},${lng}`;
}

/**
 * 네이버 지도에서 상호(업체명)로 검색한 결과를 여는 URL.
 * placeName: 상호명, address: 주소.
 */
export function getSearchByPlaceNameUrl(placeName, address) {
  if (!placeName || typeof placeName !== 'string') return null;
  const name = placeName.trim();
  if (!name) return null;
  
  // 주소 + 상호명 순서로 조합하여 검색
  const parts = [];
  if (address && String(address).trim()) {
    const addr = String(address).trim();
    parts.push(addr);
  }
  parts.push(name);
  
  const q = parts.join(' ');
  // 네이버 지도 검색 URL 형식: https://map.naver.com/v5/search/{검색어}
  return `https://map.naver.com/v5/search/${encodeURIComponent(q)}`;
}


function initializeRoadviewInSidebar(containerId, lat, lng) {
  if (!window.kakao?.maps?.RoadviewClient) return;

  const container = document.getElementById(containerId);
  if (!container) return;

  const position = new window.kakao.maps.LatLng(lat, lng);
  const roadview = new window.kakao.maps.Roadview(container);
  const roadviewClient = new window.kakao.maps.RoadviewClient();

  roadviewClient.getNearestPanoId(position, 200, function(panoId) {
    if (panoId === null) {
      container.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;width:100%;height:100%;background:linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);color:#6b7280;text-align:center;">
          <div style="font-size:32px;margin-bottom:8px;">🗺️</div>
          <div style="font-size:14px;font-weight:500;">로드뷰를 사용할 수 없습니다</div>
        </div>
      `;
      return;
    }

    roadview.setPanoId(panoId, position);

    window.kakao.maps.event.addListener(roadview, 'init', function() {
      setTimeout(() => {
        const loadingIndicator = container.querySelector('.roadview-loading');
        if (loadingIndicator) loadingIndicator.style.display = 'none';
      }, 500);
    });
  });
}

export function PropertySidebar({ property, isOpen, onClose, onFocusMap, onLocalFocus, onAddInterestedProperty, onDelete, onPropertyUpdate }) {
  const { role } = useAuth();
  const isGuest = role === ROLES.GUEST;
  
  const roadviewContainerRef = React.useRef(null);
  const miniMapContainerRef = React.useRef(null);
  const miniMapRef = React.useRef(null);
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const hasFieldValue = (value) =>
    !(value === undefined || value === null || (typeof value === 'string' && value.trim() === ''));

  const normalizeTel = (value = '') => value.replace(/[^0-9]/g, '');

  const formatMoneyValue = (value) => {
    if (!hasFieldValue(value)) return '-';
    if (typeof value === 'number') return `${value}만원`;
    return value;
  };

  const formatPremiumValue = (value) => {
    if (!hasFieldValue(value) || value === 0) {
      return '무권리';
    }
    return formatMoneyValue(value);
  };

  React.useEffect(() => {
    if (isOpen && property) {
      console.log('PropertySidebar 열림', { 
        property: property?.상호 || property?.name, 
        hasOnAddInterestedProperty: !!onAddInterestedProperty 
      });
    }
  }, [isOpen, property, onAddInterestedProperty]);

  React.useEffect(() => {
    if (isOpen && property) {
      // 로드뷰 직접 렌더링
      if (roadviewContainerRef.current) {
        const containerId = `roadview-${property.id}`;
        setTimeout(() => {
          initializeRoadviewInSidebar(containerId, property.lat, property.lng);
        }, 300);
      }

      // 미니 지도 초기화
      if (miniMapContainerRef.current && window.kakao?.maps) {
        const position = new window.kakao.maps.LatLng(property.lat, property.lng);
        
        if (miniMapRef.current) {
          miniMapRef.current.setCenter(position);
          miniMapRef.current.setLevel(3);
        } else {
          miniMapRef.current = new window.kakao.maps.Map(miniMapContainerRef.current, {
            center: position,
            level: 3,
            draggable: false,
            scrollwheel: false
          });

          // 미니 지도에 마커 추가
          // eslint-disable-next-line no-unused-vars
          const marker = createKakaoMarker(miniMapRef.current, position);
        }
      }
    }

    return () => {
      if (miniMapRef.current) {
        miniMapRef.current = null;
      }
    };
  }, [isOpen, property]);

  const roadviewPageUrl = property ? getRoadviewPageUrl(property.lat, property.lng) : '';
  const searchByPlaceNameUrl = property
    ? getSearchByPlaceNameUrl(property.상호 || property.name, property.주소)
    : null;
  const roadviewContainerId = property ? `roadview-${property.id}` : '';
  
  // 상호명 + 주소로 네이버 지도에서 검색
  const handleOpenPlaceRoadview = () => {
    if (!property) return;
    
    const placeName = property.상호 || property.name;
    if (!placeName) {
      // 상호가 없으면 기존 좌표 기반 로드뷰 열기
      window.open(roadviewPageUrl, '_blank');
      return;
    }
    
    // 네이버 지도에서 상호명 + 주소로 검색
    if (searchByPlaceNameUrl) {
      window.open(searchByPlaceNameUrl, '_blank');
    } else {
      // 검색 URL이 없으면 좌표 기반 로드뷰 열기
      window.open(roadviewPageUrl, '_blank');
    }
  };

  const handleNavigateToMainMap = () => {
    if (!property || property.lat === undefined || property.lng === undefined) {
      return;
    }

    if (onLocalFocus) {
      onLocalFocus(property);
    } else if (onFocusMap) {
      onFocusMap(property);
    }

    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* 오버레이 (사이드바 뒤 어두운 배경) */}
      <div
        className={`absolute inset-0 bg-black z-40 transition-opacity duration-500 ease-in-out ${
          isOpen && property ? 'opacity-30' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      ></div>

      {/* 사이드바 */}
      <div
        className={`absolute top-0 left-0 h-full w-full md:w-2/3 bg-white shadow-2xl z-50 transform transition-transform duration-500 ease-out overflow-hidden flex flex-col border-b-2 border-orange-300 ${
          isOpen && property ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          willChange: 'transform',
          backfaceVisibility: 'hidden'
        }}
      >
        {/* 사이드바 헤더 */}
        <div className="bg-gradient-to-r from-orange-400 to-orange-500 text-white p-2 md:p-4 shadow-md flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-sm md:text-xl font-bold truncate pr-2">{property?.name || property?.상호 || ''}</h2>
            <div className="flex items-center gap-2">
              {!isGuest && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditModalOpen(true);
                  }}
                  className="text-white hover:text-blue-200 transition-colors text-xl font-bold w-7 h-7 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-20 flex-shrink-0 cursor-pointer"
                  aria-label="매물 편집"
                  title="매물 편집"
                >
                  ✏️
                </button>
              )}
              {!isGuest && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('별표 버튼 클릭됨', property);
                    if (onAddInterestedProperty) {
                      onAddInterestedProperty(property);
                    } else {
                      console.warn('onAddInterestedProperty가 전달되지 않았습니다');
                    }
                  }}
                  className="text-white hover:text-yellow-200 transition-colors text-xl font-bold w-7 h-7 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-20 flex-shrink-0 cursor-pointer"
                  aria-label="관심 매물 등록"
                  title="관심 매물 등록"
                >
                  ⭐
                </button>
              )}
              <button
                onClick={onClose}
                className="text-white hover:text-orange-200 transition-colors text-xl font-bold w-7 h-7 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-20 flex-shrink-0"
                aria-label="닫기"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        {/* 사이드바 내용 - 좌우 분할 */}
        <div className="flex flex-col md:flex-row flex-1 overflow-y-auto md:overflow-hidden">
          {/* 왼쪽: 정보 */}
          <div className="w-full md:w-1/2 md:overflow-y-auto overflow-visible p-2 md:p-4 md:border-r border-gray-200 md:border-b-0 border-b">
          {/* 기본 정보 */}
          <div className="mb-4">
            <h3 className="text-xs md:text-base font-bold text-gray-800 mb-2 md:mb-3 pb-1 border-b-2 border-orange-300">
              기본 정보
            </h3>
            <div className="grid grid-cols-2 md:flex md:flex-col gap-1.5 md:gap-2 md:space-y-2">
              {/* GUEST는 번호(id), 상권, 층수, 평수만 표시 */}
              {!isGuest && hasFieldValue(property?.['구분']) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-24 md:flex-shrink-0 text-center">구분</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property['구분']}</span>
                </div>
              )}
              {!isGuest && hasFieldValue(property?.상호) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-24 md:flex-shrink-0 text-center">상호</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.상호}</span>
                </div>
              )}
              {/* 번호 (id) - GUEST도 표시 */}
              {hasFieldValue(property?.id) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-24 md:flex-shrink-0 text-center">번호</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.id}</span>
                </div>
              )}
              {hasFieldValue(property?.상권) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-24 md:flex-shrink-0 text-center">상권</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.상권}</span>
                </div>
              )}
              {!isGuest && hasFieldValue(property?.관계인) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-24 md:flex-shrink-0 text-center">관계인</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.관계인}</span>
                </div>
              )}
              {!isGuest && hasFieldValue(property?.연락처) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-24 md:flex-shrink-0 text-center">연락처</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>
                    <a href={`tel:${normalizeTel(property.연락처)}`} className="text-orange-600 hover:underline">
                      {property.연락처}
                    </a>
                  </span>
                </div>
              )}
              {hasFieldValue(property?.['층수(해당/전체)'] || property?.['현층/총층수']) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-24 md:flex-shrink-0 text-center">
                    층수
                  </span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>
                    {formatFloorForDisplay(property['층수(해당/전체)'] || property['현층/총층수'] || '')}
                  </span>
                </div>
              )}
              {hasFieldValue(property?.평수) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-24 md:flex-shrink-0 text-center">평수</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.평수}</span>
                </div>
              )}
              {!isGuest && hasFieldValue(property?.['파일명']) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-24 md:flex-shrink-0 text-center">파일명</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property['파일명']}</span>
                </div>
              )}
            </div>
            {!isGuest && hasFieldValue(property?.주소) && (
              <div className="flex flex-col md:flex-row text-xs md:text-sm mt-2 border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">주소</span>
                <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.주소}</span>
              </div>
            )}
          </div>

          {/* 가격 정보 */}
          <div className="mb-4">
            <h3 className="text-xs md:text-base font-bold text-gray-800 mb-2 md:mb-3 pb-1 border-b-2 border-orange-300">
              가격 정보
            </h3>
            <div className="grid grid-cols-2 md:flex md:flex-col gap-1.5 md:gap-2 md:space-y-2">
              {hasFieldValue(property?.보증금) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">보증금</span>
                  <span className="text-xs md:text-sm text-gray-800 font-semibold break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{formatMoneyValue(property.보증금)}</span>
                </div>
              )}
              {hasFieldValue(property?.월세) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">월세</span>
                  <span className="text-xs md:text-sm text-gray-800 font-semibold break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{formatMoneyValue(property.월세)}</span>
                </div>
              )}
              {!isGuest && hasFieldValue(property?.관리비) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">관리비</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{formatMoneyValue(property.관리비)}</span>
                </div>
              )}
              {hasFieldValue(property?.권리금) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">권리금</span>
                  <span className="text-xs md:text-sm text-gray-800 font-semibold break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{formatPremiumValue(property.권리금)}</span>
                </div>
              )}
            </div>
          </div>

          {/* 운영 정보 - GUEST는 숨김 */}
          {!isGuest && (
            <div className="mb-4">
              <h3 className="text-xs md:text-base font-bold text-gray-800 mb-2 md:mb-3 pb-1 border-b-2 border-orange-300">
                운영 정보
              </h3>
              <div className="grid grid-cols-2 md:flex md:flex-col gap-1.5 md:gap-2 md:space-y-2">
              {hasFieldValue(property?.현업종) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">현업종</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.현업종}</span>
                </div>
              )}
              {hasFieldValue(property?.용도) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">용도</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.용도}</span>
                </div>
              )}
              {hasFieldValue(property?.화장실) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">화장실</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.화장실}</span>
                </div>
              )}
              {hasFieldValue(property?.엘베) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">엘베</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.엘베}</span>
                </div>
              )}
              {hasFieldValue(property?.주차) && (
                <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                  <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">주차</span>
                  <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.주차}</span>
                </div>
              )}
              </div>
            </div>
          )}

          {/* 담당 및 일정 */}
          {!isGuest && (hasFieldValue(property?.담당자) || hasFieldValue(property?.담당) || hasFieldValue(property?.사용승인일) || hasFieldValue(property?.접수일자)) && (
            <div className="mb-4">
              <h3 className="text-xs md:text-base font-bold text-gray-800 mb-2 md:mb-3 pb-1 border-b-2 border-orange-300">
                담당 · 일정
              </h3>
              <div className="grid grid-cols-2 md:flex md:flex-col gap-1.5 md:gap-2 md:space-y-2">
                {hasFieldValue(property?.담당자) && (
                  <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                    <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">담당자</span>
                    <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.담당자}</span>
                  </div>
                )}
                {hasFieldValue(property?.담당) && (
                  <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                    <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">담당</span>
                    <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.담당}</span>
                  </div>
                )}
                {hasFieldValue(property?.사용승인일) && (
                  <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                    <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center" style={{ wordBreak: 'keep-all', lineBreak: 'strict' }}>입점{'\u200B'}가능일</span>
                    <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.사용승인일}</span>
                  </div>
                )}
                {hasFieldValue(property?.접수일자) && (
                  <div className="flex flex-col md:flex-row text-xs md:text-sm border border-gray-200 rounded-lg p-1.5 md:p-2 bg-gray-50">
                    <span className="text-[0.875rem] md:text-sm text-gray-600 font-medium mb-0.5 md:mb-0 md:w-20 md:flex-shrink-0 text-center">접수일자</span>
                    <span className="text-xs md:text-sm text-gray-800 break-normal text-center md:text-left" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>{property.접수일자}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 특이사항 */}
          {!isGuest && hasFieldValue(property?.특이사항) && (
            <div className="mb-4">
              <h3 className="text-xs md:text-base font-bold text-gray-800 mb-2 pb-1 border-b-2 border-orange-300">
                특이사항
              </h3>
              <div className="text-xs md:text-sm text-gray-800 leading-relaxed break-normal" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>
                {property.특이사항}
              </div>
            </div>
          )}

          {/* 위치 - GUEST는 숨김 */}
          {!isGuest && (
            <div className="mb-4">
              <h3 className="text-xs md:text-base font-bold text-gray-800 mb-2 pb-1 border-b-2 border-orange-300">
                위치
              </h3>
              <div className="relative w-full h-40 md:h-48 rounded-lg border border-gray-200 overflow-hidden bg-white">
                <div
                  ref={miniMapContainerRef}
                  className="w-full h-full"
                ></div>
                {(onLocalFocus || onFocusMap) && (
                  <>
                    <button
                      type="button"
                      onClick={handleNavigateToMainMap}
                      aria-label="메인 지도에서 위치 보기"
                      className="absolute inset-0 z-10 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 bg-transparent"
                    />
                    <div className="absolute bottom-1 md:bottom-2 right-1 md:right-2 z-20 pointer-events-none">
                      <div className="flex items-center gap-0.5 md:gap-1 bg-white/85 text-[0.875rem] md:text-xs text-gray-700 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full shadow">
                        <svg
                          className="w-3 h-3 md:w-3.5 md:h-3.5 text-orange-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 2c2.761 0 5 2.239 5 5 0 3.5-5 9-5 9s-5-5.5-5-9c0-2.761 2.239-5 5-5z"
                          />
                          <circle cx="12" cy="7" r="1.5" />
                        </svg>
                        <span className="hidden md:inline">메인 지도에서 보기</span>
                        <span className="md:hidden">지도</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          </div>

          {/* 오른쪽: 로드뷰 - GUEST는 숨김 */}
          {!isGuest && (
            <div className="w-full md:w-1/2 flex flex-col md:overflow-hidden">
           

            {/* 로드뷰 - 항상 최대 공간 사용 */}
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="p-2 md:p-4 border-b border-gray-200 flex-shrink-0 flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-xs md:text-base font-bold text-gray-800 pb-1 border-b-2 border-orange-300">
                  로드뷰
                </h3>
                <button
                  onClick={handleOpenPlaceRoadview}
                  className="px-2 md:px-3 py-1 md:py-1.5 bg-orange-500 text-white text-[0.875rem] md:text-xs rounded hover:bg-orange-600 transition-colors flex items-center gap-1"
                  title={property?.상호 || property?.name 
                    ? "네이버 지도에서 해당 업체 검색하기" 
                    : "해당 좌표 로드뷰를 새 창에서 크게 보기"}
                >
                  <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                  </svg>
                  <span>{property?.상호 || property?.name ? '네이버로 검색' : '크게 보기'}</span>
                </button>
              </div>
              <div 
                className="relative w-full flex-1 min-h-0 bg-white" 
                style={{ minHeight: '200px' }}
              >
                {/* 로드뷰 컨테이너 */}
                <div
                  id={roadviewContainerId}
                  ref={roadviewContainerRef}
                  className="w-full h-full"
                >
                  {/* 로딩 중 표시 */}
                  <div className="roadview-loading absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600 z-10">
                    <div className="text-2xl md:text-3xl mb-1 md:mb-2 animate-pulse">🗺️</div>
                    <div className="text-xs md:text-sm font-medium">로드뷰 로딩 중...</div>
                  </div>
                </div>
              </div>

              {/* 삭제 버튼 - 로드뷰 아래 - GUEST는 숨김 */}
              {onDelete && !isGuest && (
                <div className="p-2 md:p-4 border-t border-gray-200 flex-shrink-0">
                  <button
                    onClick={async () => {
                      if (!property?.id) return;
                      
                      const confirmed = window.confirm(
                        `정말 "${property?.상호 || property?.name || '이 매물'}"을(를) 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`
                      );
                      
                      if (confirmed) {
                        try {
                          await onDelete(property.id);
                          if (onClose) {
                            onClose();
                          }
                        } catch (error) {
                          console.error('매물 삭제 실패:', error);
                          alert('매물 삭제에 실패했습니다.');
                        }
                      }
                    }}
                    className="w-full py-2 md:py-2.5 px-3 md:px-4 bg-red-500 hover:bg-red-600 text-white text-xs md:text-sm font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center gap-1.5 md:gap-2"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>매물 삭제</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* 매물 편집 모달 */}
      <AddCard
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => {
          setIsEditModalOpen(false);
          if (onPropertyUpdate) {
            onPropertyUpdate();
          }
          if (onClose) {
            onClose();
          }
        }}
        existingProperties={[]}
        initialData={property}
        isEditMode={true}
      />
    </>
  );
}


